import { Utils } from './utils';
import { DeviceConfiguration, DeviceProfile } from '../controllers/device';

export class PayloadValidation {
  private _validationResponse: ValidationResponse;

  /**
   * @description Initialize response to be sent.
   * @private
   */
  private initResponse(): void {
    this._validationResponse = {
      validated: false,
      message: '',
      statusCode: 500,
    };
  }

  /**
   * Validate profile data within payload.
   * @param {DeviceProfile} profilePayload
   * @returns {boolean}
   * @private
   */
  private validateProfilePayload(profilePayload: DeviceProfile): boolean {
    const missingValues = [];

    if (!Utils.getValue(() => profilePayload.idk, false)) {
      missingValues.push('idk');
    }

    if (!Utils.getValue(() => profilePayload.serviceId, false)) {
      missingValues.push('serviceId');
    }

    if (!Utils.getValue(() => profilePayload.tenantId, false)) {
      missingValues.push('tenantId');
    }

    if (!Utils.getValue(() => profilePayload.ruleset, false)) {
      missingValues.push('ruleset');
    }

    if (!Utils.getValue(() => profilePayload.rulesetAnon, false)) {
      missingValues.push('rulesetAnon');
    }

    if (missingValues.length > 0) {
      this._validationResponse.statusCode = 400;
      this._validationResponse.validated = false;
      this._validationResponse.message =
        'Validation Error: If no "profileId" provided, "profile" payload should include(' +
        missingValues.join(', ') +
        ').';
      return this._validationResponse.validated;
    }
    return true;
  }

  /**
   * Validate config provided
   * @param {DeviceConfiguration} config
   * @returns {ValidationResponse}
   */
  public validatePayload(config: DeviceConfiguration): ValidationResponse {
    // Initialize payload and response
    this.initResponse();

    // Either profile or profileId are necessary to call device/info
    if (
      (config.profile === null && config.profileId === null) ||
      (config.profile === undefined && config.profileId === undefined)
    ) {
      this._validationResponse.statusCode = 400;
      this._validationResponse.message = 'Validation Error: "profileId" and/or "profile" object values expected.';
      return this._validationResponse;
    }

    // OnlineId existence verification
    if (config.onlineId === undefined || config.onlineId === null) {
      this._validationResponse.statusCode = 400;
      this._validationResponse.message = 'Validation Error: "onlineId" value expected.';
      return this._validationResponse;
    }

    // Validate profile
    if (config.profile && !config.profileId) {
      if (!this.validateProfilePayload(config.profile)) return this._validationResponse;
    }

    // Validate version
    if (config.version && (isNaN(config.version) || config.version <= 0)) {
      this._validationResponse.statusCode = 400;
      this._validationResponse.message = 'Validation Error: "version" is invalid.';
      return this._validationResponse;
    }

    this._validationResponse.validated = true;

    return this._validationResponse;
  }
}

export interface ValidationResponse {
  validated: boolean;
  message: string;
  statusCode: number;
}
